// Écran « Licences » : licences payées sans être attribuées, attribuées à des comptes désactivés ou inactifs, et
// attribuées au-delà du contrat (écart au contrat, pas une économie). Les montants viennent du moteur d'Ekera, en
// fourchette quand le contrat ne donne pas le prix de chaque référence ; chaque constat cite les lignes des pièces.
import { useQuery } from '@tanstack/react-query';
import type { ConstatLicenceVue } from '../api/types';
import { Attendre, Citations, Etiquette } from '../composants/communs';
import { useEkera } from '../contexte';
import { estEconomie, LIBELLE_METHODE, LIBELLE_NATURE_LICENCE } from '../domaine/documents';
import { dateFr, eurosLong, fourchette } from '../domaine/format';

export function EcranLicences() {
  const { client, cid } = useEkera();
  const q = useQuery({ queryKey: ['carte', cid, 'licences'], queryFn: () => client.licences(cid) });
  return (
    <div className="pile">
      <p className="sous-titre">
        Ekera rapproche les licences attribuées dans l’annuaire (comptes, état, dernière connexion) des contrats d’achat. Les
        montants sont calculés par le moteur à partir du contrat cité ; aucun prix n’est supposé.
      </p>
      <Attendre etats={[q]} quoi="des licences">
        {() => {
          const v = q.data!;
          if (!v.disponible)
            return <p className="encadre info">Aucun rapprochement des licences n’est encore chargé pour cette carte.</p>;
          const constats = v.constats ?? [];
          const economies = constats.filter(estEconomie);
          const ecarts = constats.filter((c) => !estEconomie(c));
          const payees = economies.filter((c) => c.nature === 'achetee_non_attribuee').reduce((n, c) => n + c.nombre, 0);
          return (
            <>
              <div className="kpis k3">
                <div className="kpi bien">
                  <span className="l">Économie annuelle possible</span>
                  <span className="v">{v.economie_annuelle && v.economie_annuelle.length === 2 ? fourchette(v.economie_annuelle[0]!, v.economie_annuelle[1]!) : 'non calculable'}</span>
                  <span className="d">moteur d’Ekera, par an</span>
                </div>
                <div className="kpi">
                  <span className="l">Licences payées, non attribuées</span>
                  <span className="v">{payees}</span>
                  <span className="d">{economies.length} constat{economies.length > 1 ? 's' : ''} d’économie</span>
                </div>
                <div className="kpi alerte">
                  <span className="l">Attribuées hors contrat</span>
                  <span className="v">{ecarts.reduce((n, c) => n + c.nombre, 0)}</span>
                  <span className="d">écart au contrat, montant non calculé</span>
                </div>
              </div>
              <p className="note">
                Situation au {dateFr(v.date_reference)} (chargée le {dateFr(v.charge_le)}).
                {v.notes?.length ? ` ${v.notes.join(' ')}` : ''}
              </p>
              <section className="panneau" aria-labelledby="t-eco">
                <h2 id="t-eco">Licences inutilisées</h2>
                {economies.length ? economies.map((c, i) => <Constat key={`${c.nature}-${c.reference}-${i}`} c={c} />) : <p className="muted">Aucune licence inutilisée retrouvée.</p>}
              </section>
              {ecarts.length > 0 && (
                <section className="panneau" aria-labelledby="t-ecarts">
                  <h2 id="t-ecarts">Attribuées au-delà du contrat</h2>
                  <p className="note">Plus de licences attribuées qu’achetées : à régulariser avec l’éditeur ou à retirer ; ce n’est pas une économie.</p>
                  {ecarts.map((c, i) => (
                    <Constat key={`${c.nature}-${c.reference}-${i}`} c={c} />
                  ))}
                </section>
              )}
              <section className="panneau" aria-labelledby="t-decompte">
                <h2 id="t-decompte">Décompte par référence</h2>
                <div className="tableau-defile">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Référence</th>
                        <th scope="col" style={{ textAlign: 'right' }}>
                          Achetées
                        </th>
                        <th scope="col" style={{ textAlign: 'right' }}>
                          Attribuées
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys({ ...v.attribuees, ...v.achetees })
                        .sort((a, b) => a.localeCompare(b, 'fr'))
                        .map((r) => (
                          <tr key={r}>
                            <td>{r}</td>
                            <td className="num">{v.achetees?.[r] ?? '—'}</td>
                            <td className="num">{v.attribuees?.[r] ?? 0}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          );
        }}
      </Attendre>
    </div>
  );
}

function Constat({ c }: { c: ConstatLicenceVue }) {
  const m = c.montant_annuel;
  const citations = c.citations ?? [];
  const calculable = m && m[0] !== null && m[1] !== null && m[0] !== undefined && m[1] !== undefined;
  return (
    <article className="option" aria-label={`${LIBELLE_NATURE_LICENCE[c.nature] ?? c.nature} : ${c.reference}`}>
      <div className="panneau-tete">
        <span className="ligne" style={{ gap: 8 }}>
          <Etiquette ton={estEconomie(c) ? 'attention' : 'grave'}>{LIBELLE_NATURE_LICENCE[c.nature] ?? c.nature}</Etiquette>
          <h3>
            {c.nombre} × {c.reference}
          </h3>
        </span>
        <span className={calculable ? 'chiffre-licence num' : 'chiffre-licence muted'}>
          {calculable
            ? m![0] === m![1]
              ? `${eurosLong(m![0] as number)} par an`
              : `${eurosLong(m![0] as number)} à ${eurosLong(m![1] as number)} par an`
            : 'montant non calculé'}
        </span>
      </div>
      <p>{c.texte}</p>
      {calculable && c.methode && (
        <p className="note">
          Calcul du moteur : {LIBELLE_METHODE[c.methode] ?? c.methode}
          {c.contrat ? `, contrat ${c.contrat}` : ''}.
        </p>
      )}
      {c.hypotheses?.length ? (
        <ul className="liste-simple note">
          {c.hypotheses.map((h) => (
            <li key={h}>Hypothèse : {h}</li>
          ))}
        </ul>
      ) : null}
      {c.comptes?.length ? (
        <details>
          <summary>
            {c.comptes.length} compte{c.comptes.length > 1 ? 's' : ''} concerné{c.comptes.length > 1 ? 's' : ''}
          </summary>
          <p className="note num">{c.comptes.join(', ')}</p>
        </details>
      ) : null}
      <details>
        <summary>
          Sources ({citations.length} ligne{citations.length > 1 ? 's' : ''} citée{citations.length > 1 ? 's' : ''})
        </summary>
        <Citations citations={citations} />
      </details>
    </article>
  );
}
